import { useEffect, useState } from 'react';
import { dataCacheKey, useCityExplorerData } from '../CityExplorerDataContext';

// Static per state for a browser tab; shared by the calculator and the map.
// The in-flight Promise is shared too, so React Strict Mode's double mount and
// the map + calculator loading together make a single request. Keys include
// the data client so a share link never reuses logged-in data.
const cache = new Map();
const inflight = new Map();

export function fetchRiskCalculatorData(client, locationId) {
  const key = dataCacheKey(client, locationId);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);
  const request = client
    .getRiskCalculatorData(locationId)
    .then((data) => {
      const result = {
        meta: data?.meta || {},
        charters: data?.charters || [],
        peers: data?.peers || [],
      };
      cache.set(key, result);
      inflight.delete(key);
      return result;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });
  inflight.set(key, request);
  return request;
}

const EMPTY = { meta: {}, charters: [], peers: [] };

export default function useRiskCalculatorData(locationId, { enabled = true } = {}) {
  const client = useCityExplorerData();
  const key = locationId ? dataCacheKey(client, locationId) : null;
  const [state, setState] = useState(() => {
    const cached = key ? cache.get(key) : null;
    return cached
      ? { loading: false, error: null, ...cached }
      : { loading: Boolean(locationId && enabled), error: null, ...EMPTY };
  });

  useEffect(() => {
    if (!locationId || !enabled) return undefined;
    if (cache.has(key)) {
      setState({ loading: false, error: null, ...cache.get(key) });
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    fetchRiskCalculatorData(client, locationId)
      .then((result) => {
        if (!cancelled) setState({ loading: false, error: null, ...result });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({ loading: false, error: err.message || 'Failed to load risk data', ...EMPTY });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [locationId, key, client, enabled]);

  return state;
}
